import { ClientQuotationsApi } from './clientQuotationsApi';
import {
  normalizeMaintenanceType,
  getMaintenanceExecutionLabel,
} from '../../../shared/utils/maintenanceTypeHelpers';
import { normalizeText } from '../../../shared/utils/textNormalizationHelpers';
import {
  groupQuotesByStatus as groupQuotesByStatusHelper,
  getQuotationStatusVariant,
} from '../../../shared/utils/quotationStatusHelpers';

function safeParseJson(value) {
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function readNameFromItem(item) {
  if (!item) return null;
  if (typeof item === 'string') return item.trim();
  if (typeof item !== 'object') return null;

  return (
    item.nome ||
    item.name ||
    item.equipamento_nome ||
    item.equipment_name ||
    item.produto ||
    item.product ||
    null
  );
}

function collectNamesFromArray(source, targetSet) {
  if (!Array.isArray(source)) return;

  source.forEach((item) => {
    const name = readNameFromItem(item);
    if (name) targetSet.add(name);
  });
}

function extractProductNames(quotation = {}) {
  const namesSet = new Set();

  collectNamesFromArray(quotation.equipamentos, namesSet);
  collectNamesFromArray(quotation.equipamentos_lista, namesSet);
  collectNamesFromArray(quotation.products, namesSet);

  if (quotation.product) {
    const productName = readNameFromItem(quotation.product);
    if (productName) namesSet.add(productName);
  }

  if (Array.isArray(quotation.itens)) {
    collectNamesFromArray(quotation.itens, namesSet);
  }

  const parsedItens = safeParseJson(quotation.itens);
  if (parsedItens) {
    collectNamesFromArray(parsedItens.equipamentos, namesSet);
    collectNamesFromArray(parsedItens.equipamentos_lista, namesSet);
  }

  return Array.from(namesSet).filter(Boolean);
}

function formatQuotationData(quotation = {}) {
  const endereco = quotation.address || quotation.endereco;
  const rawMaintenanceType =
    quotation.tipoManutencao || quotation.maintenance_type || quotation.maintenanceType;

  const maintenanceType = normalizeMaintenanceType({
    tipoManutencao: rawMaintenanceType,
    maintenanceLocalization: quotation.maintenanceLocalization,
    endereco,
  });

  const productNames = extractProductNames(quotation);
  const normalizedProductNames = productNames.map((name) => normalizeText(name));

  return {
    id: quotation.id,
    tipo: quotation.type || quotation.tipo,
    refId: quotation.reference_id || quotation.ref_id || quotation.refId,
    createdAt: quotation.created_at || quotation.createdAt,
    valor: quotation.value || quotation.valor,
    status: quotation.status,
    descricao: quotation.description || quotation.descricao,
    endereco,
    servicos: quotation.servicos || [],
    itens: quotation.itens || [],
    equipmentIds: quotation.equipmentIds || [],
    maintenanceType,
    maintenanceExecutionLabel: getMaintenanceExecutionLabel({
      tipoManutencao: rawMaintenanceType,
      maintenanceLocalization: quotation.maintenanceLocalization,
      endereco,
    }),
    productNames,
    normalizedProductNames,
  };
}

function formatQuotationsList(data) {
  if (data?.atencao !== undefined && data?.andamento !== undefined && data?.concluidos !== undefined) {
    const allQuotes = [];
    allQuotes.push(...(data.atencao || []).map((quotation) => formatQuotationData(quotation)));
    allQuotes.push(...(data.andamento || []).map((quotation) => formatQuotationData(quotation)));
    allQuotes.push(...(data.concluidos || []).map((quotation) => formatQuotationData(quotation)));
    return allQuotes;
  }

  return Array.isArray(data) ? data.map((quotation) => formatQuotationData(quotation)) : [];
}

function formatarData(dataString) {
  if (!dataString) return '--';

  if (typeof dataString === 'string' && dataString.includes('/')) {
    return dataString;
  }

  const data = new Date(dataString);
  return data.toLocaleDateString('pt-BR');
}

function formatarValor(valor, status) {
  if (status && normalizeText(status) === 'aguardando orcamento') return 'Aguardando orçamento';
  if (!valor && valor !== 0) return 'Aguardando orçamento';

  const numValue = typeof valor === 'string' ? parseFloat(valor) : valor;
  if (Number.isNaN(numValue)) return 'Valor inválido';

  return numValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function groupQuotesByStatus(quotes) {
  return groupQuotesByStatusHelper(quotes);
}

function getStatusVariant(status) {
  return getQuotationStatusVariant(status);
}

function getQuotations() {
  return ClientQuotationsApi.getQuotations().then((response) => formatQuotationsList(response.data));
}

function getQuotationDetails(tipo, id) {
  return ClientQuotationsApi.getQuotationDetails(tipo, id).then((response) =>
    formatQuotationData(response.data)
  );
}

function createQuotation(data) {
  return ClientQuotationsApi.createQuotation(data).then((response) =>
    formatQuotationData(response.data)
  );
}

function updateQuotation(id, data) {
  return ClientQuotationsApi.updateQuotation(id, data).then((response) =>
    formatQuotationData(response.data)
  );
}

function deleteQuotation(id) {
  return ClientQuotationsApi.deleteQuotation(id);
}

export const ClientQuotationsService = {
  getQuotations,
  getQuotationDetails,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  formatQuotationData,
  formatQuotationsList,
  formatarData,
  formatarValor,
  groupQuotesByStatus,
  getStatusVariant,
  normalizeText,
  extractProductNames,
};

